import {SetURLSearchParams, useSearchParams} from "react-router-dom";
import {SetState} from "./FormHandlers";
import {useEffect, useState} from "react";
import {INeighbourhood, KeyVault, Office} from "../api/Model";
import {Residence, ResidenceModel} from "../api/ResidenceStore";
import {Opt} from "./Optional";
import {DateRangeOption, DateRangeOptions} from "../components/filter-fields/DateRangeFilter";

interface FilterFieldSerializer<Filter extends {}> {
    readonly field: keyof Filter & string;

    serialize(filter: Filter, emptyFilter: Filter): string | undefined

    deserialize(filter: Filter, strValue: string): void
}

class StringFilterFieldSerializer<Filter extends {}> implements FilterFieldSerializer<Filter> {

    constructor(
        readonly field: keyof Filter & string,
        private readonly getValue: (filter: Filter) => Opt<string>,
        private readonly setValue: (filter: Filter, value: string) => void,
    ) {
    }

    serialize(filter: Filter, emptyFilter: Filter): string | undefined {
        const value = this.getValue(filter);
        const emptyValue = this.getValue(emptyFilter);
        return value && value !== emptyValue && value.trim().length > 0 ? value.trim() : undefined;
    }

    deserialize(filter: Filter, strValue: string) {
        return this.setValue(filter, strValue);
    }
}

class NumberFilterFieldSerializer<Filter extends {}> implements FilterFieldSerializer<Filter> {

    constructor(
        readonly field: keyof Filter & string,
        private readonly getValue: (filter: Filter) => Opt<number>,
        private readonly setValue: (filter: Filter, value: number) => void,
    ) {
    }

    serialize(filter: Filter, emptyFilter: Filter): string | undefined {
        const value = this.getValue(filter);
        const emptyValue = this.getValue(emptyFilter);
        return value !== null && value !== undefined && value !== emptyValue ? value.toString() : undefined;
    }

    deserialize(filter: Filter, strValue: string) {
        const value = Number(strValue);
        if (!Number.isNaN(value)) {
            this.setValue(filter, value);
        }
    }
}

class BooleanFilterFieldSerializer<Filter extends {}> implements FilterFieldSerializer<Filter> {

    constructor(
        readonly field: keyof Filter & string,
        private readonly getValue: (filter: Filter) => Opt<boolean>,
        private readonly setValue: (filter: Filter, value: boolean) => void,
    ) {
    }

    serialize(filter: Filter, emptyFilter: Filter): string | undefined {
        const value = filter[this.field] as Opt<boolean>;
        const emptyValue = emptyFilter[this.field] as Opt<boolean>;
        return value !== null && value !== undefined && value !== emptyValue ? value.toString() : undefined;
    }

    deserialize(filter: Filter, strValue: string): void {
        this.setValue(filter, strValue === 'true' || strValue === '1');
    }
}

class DateRangeFilterFieldSerializer<Filter extends {}> implements FilterFieldSerializer<Filter> {

    constructor(
        readonly field: keyof Filter & string,
        private readonly getValue: (filter: Filter) => Opt<DateRangeOption>,
        private readonly setValue: (filter: Filter, value: DateRangeOption) => void,
    ) {
    }

    serialize(filter: Filter, emptyFilter: Filter): string | undefined {
        const value = this.getValue(filter);
        const emptyValue = this.getValue(emptyFilter);
        return value !== null && value !== undefined && value !== emptyValue ? DateRangeOptions.encode(value) : undefined;
    }

    deserialize(filter: Filter, strValue: string) {
        const value = DateRangeOptions.decode(strValue);
        if (value) {
            this.setValue(filter, value);
        }
    }
}


type ValueGetter<Filter extends {}, T> = (filter: Filter) => Opt<T>;
type ValueSetter<Filter extends {}, T> = (filter: Filter, value: T) => void;

function defaultGetter<Filter extends {}, T>(property: keyof Filter): ValueGetter<Filter, T> {
    return (filter: Filter) => filter[property] as Opt<T>;
}

function defaultSetter<Filter extends {}, T>(property: keyof Filter): ValueSetter<Filter, T> {
    return (filter: Filter, value: T) => filter[property] = value as Filter[keyof Filter & string];
}


class FilterSerializerBuilder<Filter extends {}> {
    readonly fieldSerializers: FilterFieldSerializer<Filter>[] = [];


    str(
        property: keyof Filter & string,
        getValue: ValueGetter<Filter, string> = defaultGetter(property),
        setValue: ValueSetter<Filter, string> = defaultSetter(property),
    ) {
        this.fieldSerializers.push(new StringFilterFieldSerializer(property, getValue, setValue));
    }

    strArray(
        property: keyof Filter & string,
        getValue: ValueGetter<Filter, string[]> = defaultGetter(property),
        setValue: ValueSetter<Filter, string[]> = defaultSetter(property),
    ) {
        this.str(
            property,
            filter => getValue(filter)?.join(','),
            (filter, value) => setValue(filter, value.split(',')),
        );
    }

    num(
        property: keyof Filter & string,
        getValue: ValueGetter<Filter, number> = defaultGetter(property),
        setValue: ValueSetter<Filter, number> = defaultSetter(property),
    ) {
        this.fieldSerializers.push(new NumberFilterFieldSerializer(property, getValue, setValue));
    }

    numId<Entity extends { id: number }>(
        property: keyof Filter & string,
        allEntities: () => Entity[],
        getValue: ValueGetter<Filter, Entity> = defaultGetter(property),
        setValue: ValueSetter<Filter, Entity> = defaultSetter(property),
    ) {
        this.num(
            property,
            filter => getValue(filter)?.id,
            (filter, value) => {
                const resolvedEntity = allEntities().find(e => e.id === value);
                if (resolvedEntity) {
                    setValue(filter, resolvedEntity);
                }
            },
        );
    }

    dateRange(
        property: keyof Filter & string,
        getValue: ValueGetter<Filter, DateRangeOption> = defaultGetter(property),
        setValue: ValueSetter<Filter, DateRangeOption> = defaultSetter(property),
    ) {
        this.fieldSerializers.push(new DateRangeFilterFieldSerializer(property, getValue, setValue));
    }

    keyVault(
        property: keyof Filter & string,
        keyVaults: KeyVault[],
        getValue: ValueGetter<Filter, KeyVault | undefined> = defaultGetter(property),
        setValue: ValueSetter<Filter, KeyVault | undefined> = defaultSetter(property),
    ) {
        this.num(
            property,
            filter => getValue(filter)?.id,
            (filter, value) => {
                const keyVault = keyVaults.find(it => it.id === value);
                if (keyVault) {
                    setValue(filter, keyVault);
                }
            },
        );
    }

    residency(
        property: keyof Filter & string,
        residenceModel: ResidenceModel,
        getValue: ValueGetter<Filter, Residence | undefined> = defaultGetter(property),
        setValue: ValueSetter<Filter, Opt<Residence>> = defaultSetter(property),
    ) {
        this.str(
            property,
            filter => {
                const residence = getValue(filter);
                return residence == null ? 'all' : ResidenceModel.serialize(residence);
            },
            (filter, value) => {
                if (value === 'all') {
                    setValue(filter, null);
                    return;
                }
                const residence = residenceModel.deserialize(value);
                if (residence) {
                    setValue(filter, residence);
                }
            },
        );
    }

    office(
        property: keyof Filter & string,
        offices: Office[],
        getValue: ValueGetter<Filter, Office | undefined> = defaultGetter(property),
        setValue: ValueSetter<Filter, Office | undefined> = defaultSetter(property),
    ) {
        this.num(
            property,
            filter => getValue(filter)?.id,
            (filter, value) => {
                const site = offices.find(it => it.id === value);
                if (site) {
                    setValue(filter, site);
                }
            },
        );
    }

    neighbourhoods(
        property: keyof Filter & string,
        allNeighbourhoods: INeighbourhood[],
        getValue: ValueGetter<Filter, INeighbourhood[]> = defaultGetter(property),
        setValue: ValueSetter<Filter, INeighbourhood[]> = defaultSetter(property),
    ) {
        this.strArray(
            property,
            filter => getValue(filter)?.map(it => it.key),
            (filter, value) => {
                setValue(filter, allNeighbourhoods.filter(it => value.includes(it.key)));
            },
        );
    }

    bool(
        property: keyof Filter & string,
        getValue: ValueGetter<Filter, boolean> = defaultGetter(property),
        setValue: ValueSetter<Filter, boolean> = defaultSetter(property),
    ) {
        this.fieldSerializers.push(new BooleanFilterFieldSerializer(property, getValue, setValue));
    }
}

export class FilterSerializer<Filter extends {}> {

    constructor(
        readonly emptyFilter: Filter,
        private readonly fieldSerializers: FilterFieldSerializer<Filter>[],
    ) {
    }


    serialize(filter: Filter): Record<string, string> {
        const serialized = {} as Record<string, string>;
        this.fieldSerializers.forEach(serializer => {
            const value = serializer.serialize(filter, this.emptyFilter);
            if (value !== undefined) {
                serialized[serializer.field] = value;
            }
        });
        return serialized as Record<string, string>;
    }

    private paramsEqual(a: URLSearchParams, b: Record<string, string>): boolean {
        for (const fieldSerializer of this.fieldSerializers) {
            const av = a.get(fieldSerializer.field) || '';
            const bv = b[fieldSerializer.field] || '';
            if (av !== bv) return false;
        }
        return true;
    };

    serializeToUrl(filter: Filter, searchParams: URLSearchParams, setSearchParams: SetURLSearchParams) {
        const nextParams = this.serialize(filter);
        if (!this.paramsEqual(searchParams, nextParams)) {
            const prevParams = Object.fromEntries(searchParams.entries());
            for (const fieldSerializer of this.fieldSerializers) {
                delete prevParams[fieldSerializer.field];
            }
            setSearchParams({...prevParams, ...nextParams}, {replace: false});
        }
    }


    deserialize(queryParams: URLSearchParams): Filter {
        const deserialized: Filter = {...this.emptyFilter};

        this.fieldSerializers.forEach(serializer => {
            const strValue = queryParams.get(serializer.field);
            if (strValue !== null) {
                serializer.deserialize(deserialized, strValue);
            }
        });

        return deserialized;
    }

    deserializeFromUrl(searchParams: URLSearchParams, filter: Filter, setFilter: SetState<Filter>) {
        const prevParams = this.serialize(filter);
        if (!this.paramsEqual(searchParams, prevParams)) {
            const next = this.deserialize(searchParams);
            setFilter(next);
        }
    }
}

export function filterSerializer<Filter extends {}>(emptyFilter: Filter, build: (b: FilterSerializerBuilder<Filter>) => void): FilterSerializer<Filter> {
    const builder = new FilterSerializerBuilder<Filter>();
    build(builder);
    return new FilterSerializer<Filter>(emptyFilter, builder.fieldSerializers);
}

export function useFilter<Filter extends {}>(serializer: FilterSerializer<Filter>): [Filter, SetState<Filter>] {
    const [searchParams, setSearchParams] = useSearchParams();
    const [filter, setFilter] = useState(serializer.deserialize(searchParams));

    useEffect(() => {
        setFilter(serializer.deserialize(searchParams));
    }, [serializer, searchParams, setFilter]);

    useEffect(() => {
        serializer.deserializeFromUrl(searchParams, filter, setFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        serializer.serializeToUrl(filter, searchParams, setSearchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    return [filter, setFilter];
}
